/**
 * Ukrainian message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: ви, lowercase (locked in l10n/locales.md).
 *
 * Platform terms follow Ukrainian UI conventions (налаштування,
 * створення пари for pairing, дублювання for notification mirroring,
 * видалити for dismiss, розмова for conversation, приймач for surface).
 * "Glance" is the untranslated feature brand name (glossary D1); «кліп»
 * renders the D2 loanword in Cyrillic (locked CJK/Cyrillic clarification).
 *
 * Plural notes: Ukrainian is a {one, few, many, other} CLDR locale —
 * every count-bearing `selectPlural("uk", …)` supplies all four forms.
 * `one` covers 1, 21, 31, 41… (not just 1), so numbers are never
 * hardcoded in the `one` form.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Підключено",
    statusReconnecting: "Перепідключення…",
    statusDisconnected: "Відключено",
    openSettings: "Відкрити налаштування",
    pairedFallbackDevice: "ваш телефон",
    publicModeToggleEnable: "Приховати вміст сповіщень",
    publicModeToggleDisable: "Показати вміст сповіщень",
  },

  notifications: {
    replyAriaLabel: "Відповісти",
    replyPlaceholder: "Відповісти…",
    replySend: "Надіслати",
    sent: "Надіслано",
    failedToSend: "Не вдалося надіслати",
    unread: "Непрочитане",
    dismiss: "Видалити",
    notificationImageAlt: "Зображення зі сповіщення",
  },

  conversations: {
    replyAriaLabel: "Відповісти",
    replyPlaceholder: "Відповісти…",
    replySend: "Надіслати",
    sent: "Надіслано",
    failedToSend: "Не вдалося надіслати",
    dismiss: "Видалити розмову",
    messagesListLabel: "Повідомлення в цій розмові",
    deviceOwner: "Ви",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Немає непрочитаних повідомлень"
        : `${count} ${selectPlural("uk", count, {
            one: "непрочитане повідомлення",
            few: "непрочитані повідомлення",
            many: "непрочитаних повідомлень",
            other: "непрочитаного повідомлення",
          })}`,
  },

  list: {
    today: "Сьогодні",
    yesterday: "Вчора",
    searchNotifications: "Пошук сповіщень",
    clearSearch: "Очистити пошук",
    clearAll: "Видалити все",
    markAllRead: "Позначити все прочитаним",
    groupCheckboxLabel: "Групувати",
    noNotificationsTitle: "Сповіщень ще немає",
    noNotificationsDesc: "Нові сповіщення, продубльовані з телефону, з'являтимуться тут.",
    noMatchesTitle: "Нічого не знайдено",
    notificationsList: "Сповіщення",
    privacyAppList: "Сповіщення за застосунками",
    clear: "Видалити",
    clearMatches: (count: number): string =>
      `Видалити ${count} ${selectPlural("uk", count, {
        one: "збіг",
        few: "збіги",
        many: "збігів",
        other: "збігу",
      })}`,
    noMatchesDesc: (query: string): string => `Немає сповіщень за запитом «${query}».`,
    appNotificationsGroup: (appName: string): string => `Сповіщення ${appName}`,
    dismissAllFrom: (appName: string): string => `Видалити всі від ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("uk", count, {
              one: "повідомлення",
              few: "повідомлення",
              many: "повідомлень",
              other: "повідомлення",
            })
          : kind === "calls"
            ? selectPlural("uk", count, {
                one: "виклик",
                few: "виклики",
                many: "викликів",
                other: "виклику",
              })
            : selectPlural("uk", count, {
                one: "сповіщення",
                few: "сповіщення",
                many: "сповіщень",
                other: "сповіщення",
              });
      return `${count} ${noun}`;
    },
    kbdDismiss: "видалити",
    kbdNavigate: "навігація",
    kbdOrSeparator: "або",
  },

  settings: {
    back: "Назад до сповіщень",
    settings: "Налаштування",
    general: "Загальні",
    theme: "Тема",
    themeAuto: "Автоматична",
    themeLight: "Світла",
    themeDark: "Темна",
    language: "Мова",
    languageSystemDefault: "Як у системі",
    notificationsSection: "Сповіщення",
    osNotifications: "Системні сповіщення",
    callNotifications: "Сповіщення про виклики",
    privacySecurity: "Конфіденційність",
    analyticsToggle: "Анонімна статистика використання",
    analyticsToggleDescription: "Лише лічильники та віхи — ніколи ваші сповіщення.",
    statusConnected: "Підключено",
    statusReconnecting: "Перепідключення…",
    statusDisconnected: "Відключено",
    disconnectFromPhone: "Відключити від телефону",
    disconnectConfirm: "Ви впевнені? Пару з пристроєм буде розірвано.",
    yesDisconnect: "Так, відключити",
    cancel: "Скасувати",
    version: "Версія",
    encryption: "Шифрування",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Потрібна допомога? Напишіть на __EMAIL__",
    disconnectFailed: "Не вдалося відключити — спробуйте ще раз",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Відключено, але не вдалося очистити локальні дані: ${errorMsg}. Перезавантажте сторінку.`,
    disconnectLocalCleanupFailed:
      "Відключено, але не вдалося очистити локальні дані. Перезавантажте сторінку.",
  },

  pairing: {
    enterCodeInstruction: "Введіть цей код у застосунку Reflecto",
    pairedSuccessfully: "Пару успішно створено!",
    pairingFailed: "Не вдалося створити пару",
    requestingPairingCode: "Запитуємо код для створення пари…",
    codeExpired: "Термін дії коду сплив",
    tryAgain: "Спробувати ще раз",
    failedToStartPairing: "Не вдалося почати створення пари",
    installHeading: "Ще немає застосунку?",
    installScanHint: "Відскануйте камерою телефона",
    installQrLabel: "QR-код для встановлення Reflecto на телефон",
    capabilitiesHeading: "Після створення пари ви зможете:",
    capabilityMirror: "Бачити сповіщення телефона тут",
    capabilityReply: "Відповідати, не беручи телефон до рук",
    capabilityShare: "Обмінюватися буфером обміну та файлами в обидва боки",
    encryptionNote: "Наскрізне шифрування. Сервер ніколи не бачить ваш вміст.",
  },

  dnd: {
    title: "Не турбувати",
    description: "Призупинити сповіщення на цьому пристрої на найближчу годину.",
    snooze1h: "Призупинити на 1 годину",
    snoozeActiveUntil: (formattedTime: string): string => `Активно до ${formattedTime}`,
    cancelSnooze: "Скасувати",
  },

  connectors: {
    title: "Підключені сервіси",
    emptyBody:
      "Підключених сервісів поки немає. Підключіть ChatGPT або Claude в налаштуваннях їхніх конекторів, а потім підтвердьте код на телефоні.",
    scopeSend: "Може надсилати сповіщення",
    added: (date: string): string => `Додано ${date}`,
    lastUsed: (relative: string): string => `Востаннє використано ${relative}`,
    neverUsed: "Не використовувався",
    revoke: "Відключити",
    revokeConfirm: (label: string): string => `Відключити ${label}? Доступ буде втрачено негайно.`,
    revokeAction: "Відключити",
    revokeConfirmLabel: (label: string): string => `Відключити ${label}`,
    cancel: "Скасувати",
    loadError: "Не вдалося завантажити підключені сервіси. Спробуйте ще раз.",
    revokeError: "Не вдалося відключити сервіс. Спробуйте ще раз.",
    retry: "Повторити",
  },

  safetyNumber: {
    rowLabel: "Перевірити шифрування",
    rowSublabel: "Порівняйте коди безпеки зі своїм телефоном",
    cardTitle: "Код безпеки",
    caption: (deviceLabel: string): string =>
      `Порівняйте з кодом, показаним на ${deviceLabel}. Якщо коди збігаються, ваше наскрізне шифрування не пошкоджено.`,
    repairNote: "Цей код змінюється під час повторного з'єднання пристрою.",
    copy: "Копіювати",
    copied: "Скопійовано",
    maskedHint: "Приховано, поки ввімкнено режим конфіденційності",
  },

  feedback: {
    title: "Надіслати відгук",
    close: "Закрити",
    messageLabel: "Ваше повідомлення",
    messagePlaceholder: "Знайшли баг, маєте ідею чи щось не так? Розкажіть нам.",
    categoryLabel: "Категорія",
    categoryBug: "Баг",
    categoryIdea: "Ідея",
    categoryOther: "Інше",
    emailLabel: "Ел. пошта (необов'язково, лише якщо хочете отримати відповідь)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Ми не додамо вас до жодних розсилок.",
    disclosure:
      "Ваше повідомлення надсилається на сервер Reflecto і зберігається в нашому внутрішньому трекері задач. Ми додаємо версію застосунку та ідентифікатор пристрою, наданий під час створення пари, щоб зіставляти звіти. Ми не бачимо ваші сповіщення, контакти чи інші особисті дані. Ел. пошта — за бажанням: вкажіть її, лише якщо хочете отримати відповідь. Ми не додамо вас до жодних розсилок.",
    submit: "Надіслати відгук",
    sending: "Надсилаємо…",
    success: "Дякуємо — відгук прочитає людина, зазвичай протягом 24 год",
    error: "Не вдалося надіслати. Спробуйте ще раз.",
    done: "Готово",
    sendFeedback: "Надіслати відгук",
  },

  uninstall: {
    headline: "Розширення Reflecto видалено",
    body: "Облікового запису ніколи не було, а розширення зберігало дані вашої пари лише на цьому комп'ютері, тож прибирати за ним нічого.",
    ask: "Якщо у вас є тридцять секунд, ми хотіли б знати, що пішло не так.",
    categoryLegend: "Якого роду цей відгук?",
    categoryBug: "Щось зламалося",
    categoryIdea: "Бракувало того, що мені потрібно",
    categoryOther: "Щось інше",
    messageLabel: "Що пішло не так?",
    messagePlaceholder: "Через кілька годин сповіщення перестали надходити.",
    messageRequired: "Спершу напишіть речення, а тоді надсилайте.",
    charactersLeft: (count: number): string => `Залишилося ${count} символів`,
    emailLabel: "Ел. пошта (необов'язково)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Використовуємо лише щоб відповісти вам. Більше ні для чого.",
    submit: "Надіслати відгук",
    sending: "Надсилаємо…",
    retry: "Спробувати ще раз",
    privacyNote:
      "Ця форма не пов'язана з жодним пристроєм. У ній немає ваших сповіщень, буфера обміну чи файлів. Ел. пошта необов'язкова і потрібна лише для відповіді.",
    success: "Дякуємо. Кожен такий відгук читає людина.",
    errorGeneric: "Не надіслалося. Ваше повідомлення лишилося тут.",
    errorRateLimited:
      "Ви нещодавно надіслали кілька повідомлень. Спробуйте приблизно за годину. Ваш текст залишиться тут.",
    footerWebApp: "Вебзастосунок Reflecto працює й без розширення.",
    footerWebAppLink: "Відкрити вебзастосунок",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Закрити",
    aiSummaryTag: "Зведення ШІ",
    errorAi: "Зараз не вдалося скласти зведення.",
    retryCta: "Спробувати ще раз",
    downloadHeading: "Зведення від ШІ",
    downloadBody:
      "Надолужте пропущене за допомогою зведення від ШІ прямо на пристрої. Усе працює лише на вашому пристрої.",
    downloadCta: "Завантажити модель",
    downloadPrivacyNote: "Одноразове завантаження. Сервер ніколи не бачить ваші сповіщення.",
    downloadingHeading: "Завантажуємо модель…",
    caughtUp: "Ви все переглянули — зараз ніщо не потребує вашої уваги.",
    needsYou: "Потребує уваги",
    quiet: "Решта",
    scanning: (pending: number): string => `Готуємо зведення: ${pending}…`,
    jump: "Відкрити сповіщення",
    affordanceReply: "Відповісти",
    affordanceOpen: "Відкрити",
    affordanceCopyOtp: "Копіювати код",
    buttonLabel: "Glance",
  },

  update: {
    title: "Доступне оновлення",
    description: "Готова нова версія Reflecto. Перезавантажте сторінку, щоб оновитися.",
    reload: "Перезавантажити",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Ми назвали цей пристрій «${name}».`,
    cardLooksGood: "Підходить",
    cardEdit: "Змінити",
    cardAriaLabel: "Підтвердьте назву пристрою",
    renameTitle: "Перейменувати цей пристрій",
    renameLabel: "Назва пристрою",
    renameSave: "Зберегти",
    renameCancel: "Скасувати",
    renameFailed: "Не вдалося перейменувати. Спробуйте ще раз.",
  },

  collision: {
    title: (name: string): string => `У вас уже є «${name}»`,
    subtitle: "Як назвати цей пристрій?",
    chipWork: (name: string): string => `Робочий ${name}`,
    chipHome: (name: string): string => `Домашній ${name}`,
    chipPersonal: (name: string): string => `Особистий ${name}`,
    customLabel: "Власна назва",
    save: "Зберегти",
    useAnyway: "Усе одно використати початкову назву",
    saveFailed: "Не вдалося зберегти нову назву. Спробуйте ще раз.",
    ariaLabel: "Усуньте збіг назв пристроїв",
  },

  clipboard: {
    title: "Буфер обміну",
    emptyTitle: "Поки що немає кліпів",
    empty: "Кліпи з інших ваших пристроїв з'являються тут. Натисніть, щоб скопіювати.",
    from: (label: string): string => `від ${label}`,
    to: (label: string): string => `до ${label}`,
    copy: "Копіювати в буфер обміну",
    copied: "Скопійовано",
    reveal: "Показати",
    hide: "Приховати",
    maskedHint: "Вміст приховано — натисніть «Показати»",
    sensitiveChip: "Конфіденційно",
    cloudChip: "через ваш токен API",
    delete: "Видалити кліп",
    sendPlaceholder: "Вставте текст для надсилання…",
    sendCta: "Надіслати",
    sendSensitiveToggle: "Позначити як конфіденційний",
    sendSuccess: (n: number): string =>
      `Надіслано на ${n} ${selectPlural("uk", n, {
        one: "пристрій",
        few: "пристрої",
        many: "пристроїв",
        other: "пристрою",
      })}.`,
    sendPartial: (n: number, total: number): string =>
      `Надіслано на ${n} з ${total} ${selectPlural("uk", total, {
        one: "пристрою",
        few: "пристроїв",
        many: "пристроїв",
        other: "пристрою",
      })}.`,
    sendNoDevices: "Немає спарованих пристроїв для надсилання.",
    sendFailed: "Не вдалося надіслати. Спробуйте ще раз.",
    sendEmpty: "Немає чого надсилати.",
    dockRecentHeading: "Нещодавні",
    dockEmptyClipboard: "Буфер обміну порожній.",
    clearAll: "Видалити все",
    clearAllConfirm: "Так, видалити все",
    clearAllCancel: "Скасувати",
    shareTitle: "Надіслати на ваші пристрої",
    shareReview:
      "Перевірте, що надсилаєте. Дані підуть на ваші спаровані пристрої з наскрізним шифруванням.",
    shareSend: "Надіслати",
    shareCancel: "Скасувати",
    shareNotPaired: "Щоб надіслати спільний текст, спочатку створіть пару з пристроєм.",
    shareOpenApp: "Відкрити Reflecto",
    pasteFromClipboard: "Вставити з буфера обміну",
    readFailed: "Не вдалося прочитати буфер обміну — вставте текст нижче.",
  },
  install: {
    title: "Встановити Reflecto",
    body: "Додайте на головний екран для швидшого доступу.",
    cta: "Встановити",
    dismissAria: "Відхилити запит на встановлення",
  },
  nav: {
    home: "Головна",
    clipboard: "Буфер обміну",
    files: "Файли",
    ariaLabel: "Основна навігація",
    fileHiddenPublicMode: "Приховано, доки увімкнено режим приватності.",
  },
  common: {
    loading: "Завантаження",
  },
  errors: {
    notPaired: "Не спаровано",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} на ${os}`,
    webFallback: "Веб-браузер",
  },

  palette: {
    placeholder: "Введіть команду…",
    noResults: "Немає відповідних команд",
    ariaLabel: "Палітра команд",
    clearAll: "Видалити всі сповіщення",
    glance: "Зведення Glance",
    cancelSnooze: "Скасувати призупинення",
    copyClip: "Копіювати останній отриманий кліп",
  },

  fileTransfer: {
    sendTitle: "Надіслати файл",
    sendChooseFile: "Вибрати файл",
    sendDeviceLabel: "Надіслати на",
    sendPhoneFallbackName: "Телефон",
    send: "Надіслати файл",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Підготовка…" : `Підготовка — ${pct}%`,
    sendDelivering: "Доставлення на телефон…",
    sendWaitingAccept: "Очікування підтвердження на телефоні…",
    sendWaitingAppOpen: "Відкрийте Reflecto на телефоні, щоб прийняти",
    sendWaitingAppOpenHint:
      "Телефон не може отримувати push-сповіщення, тому покаже цей файл лише після відкриття Reflecto.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Завантаження частини ${partNumber} з ${partCount}…`,
    sendDone: "Надіслано.",
    sendFailedRejected: "Телефон відхилив файл.",
    sendFailedNoAnswer: "Телефон не відповів.",
    sendFailedUploadFailed: "Не вдалося завантажити. Спробуйте ще раз.",
    sendFailedCancelled: "Скасовано.",
    sendFailedGeneric: "Щось пішло не так. Спробуйте ще раз.",
    sendTooLarge: (maxMiB: number): string => `Файл завеликий (макс. ${maxMiB} MiB).`,
    sendKeepOpenHint: "Не закривайте цю вкладку, доки передавання не завершиться.",
    sendAnother: "Надіслати ще",
    sendNoDevice: "Щоб надсилати файли, спочатку створіть пару з телефоном.",
    sendCancel: "Скасувати",
    sendPeerProgress: (chunksDone: number): string => `Телефон отримав фрагментів: ${chunksDone}.`,

    receiveTitle: "Вхідні файли",
    receiveOfferFrom: (sender: string): string => `${sender} хоче надіслати вам файл`,
    receiveAccept: "Прийняти",
    receiveDecline: "Відхилити",
    receiveReceiving: (n: number, total: number): string => `Отримання частини ${n} з ${total}…`,
    receiveDone: "Збережено.",
    receiveFailed: "Передавання не вдалося.",
    receiveCancelled: "Скасовано.",
    receiveCancel: "Скасувати",

    errorChunkAuthFail: "Передавання не вдалося — дані не пройшли перевірку. Спробуйте ще раз.",
    errorHashMismatch:
      "Передавання не вдалося — файл не пройшов перевірку цілісності. Спробуйте ще раз.",
    errorStorageFull: "Недостатньо місця у сховищі для цього передавання.",
    errorExpired: "Термін дії передавання минув.",
    errorQuotaExceeded: "Досягнуто денний ліміт передавань — спробуйте знову завтра.",
    errorInternal: "Передавання не вдалося — щось пішло не так.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct} %`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Закрити",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Перебіг передавання",
    outgoing: (n: number): string => `Вихідні (${n})`,
    incoming: (n: number): string => `Вхідні (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Прийняти всі",
    dropTitle: "Перетягніть сюди",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Файли до ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Перетягніть сюди або виберіть файл для надсилання",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Напряму",
    transportDirectHint:
      "Надіслано напряму між пристроями — ніколи не зберігається на наших серверах.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Через сервер",
    transportRelayHint:
      "Передано через наші сервери, усе ще з наскрізним шифруванням — ми не можемо це прочитати.",
    directBadge: "Напряму",
  },
  time: {
    justNow: "щойно",
    minutesAgo: (count: number): string => `${count} хв тому`,
    hoursAgo: (count: number): string => `${count} год тому`,
  },
} satisfies MessagesShape;
